/**
 * Security utilities for CIPA Fácil EHS
 * Implements SHA-256 hashing with pepper for LGPD compliance
 */

// Pepper for additional security (should be in environment variables)
const CPF_PEPPER =
  process.env.REACT_APP_CPF_PEPPER || "cipa-facil-ehs-2026-secure-pepper";

/**
 * Generates a SHA-256 hash of the CPF with a pepper for security
 * Used to verify voters without storing CPF in plain text
 *
 * @param cpf - The CPF to hash (11 digits, no formatting)
 * @param salt - Optional salt (e.g., eleicao_id) for additional uniqueness
 * @returns Promise<string> - The hex-encoded SHA-256 hash
 */
export async function hashCpfWithPepper(
  cpf: string,
  salt?: string,
): Promise<string> {
  const dataToHash = cpf + CPF_PEPPER + (salt || "");
  const encoder = new TextEncoder();
  const data = encoder.encode(dataToHash);

  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  // Convert ArrayBuffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  return hashHex;
}

/**
 * Verifies if a CPF matches a stored hash
 *
 * @param cpf - The CPF to verify (11 digits, no formatting)
 * @param storedHash - The stored hash to compare against
 * @param salt - Optional salt used when creating the original hash
 * @returns Promise<boolean> - True if the CPF matches the hash
 */
export async function verifyCpfHash(
  cpf: string,
  storedHash: string,
  salt?: string,
): Promise<boolean> {
  const computedHash = await hashCpfWithPepper(cpf, salt);
  return computedHash === storedHash;
}

/**
 * Validates a CPF format (does not validate check digits)
 *
 * @param cpf - The CPF to validate
 * @returns boolean - True if the format is valid (11 numeric digits)
 */
export function isValidCpfFormat(cpf: string): boolean {
  const cleanCpf = cpf.replace(/\D/g, "");
  return cleanCpf.length === 11;
}

/**
 * Validates a CPF including check digits (Módulo 11)
 *
 * @param cpf - The CPF to validate (can include formatting)
 * @returns boolean - True if the CPF is valid
 */
export function isValidCpf(cpf: string): boolean {
  const cleanCpf = cpf.replace(/\D/g, "");

  if (cleanCpf.length !== 11) return false;

  // Check for known invalid patterns (all same digits)
  if (/^(\d)\1{10}$/.test(cleanCpf)) return false;

  // Validate first check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCpf[i]) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(cleanCpf[9])) return false;

  // Validate second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCpf[i]) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(cleanCpf[10])) return false;

  return true;
}

/**
 * Masks a CPF for display (e.g., ***.***.***-XX)
 *
 * @param cpf - The CPF to mask
 * @returns string - The masked CPF showing only last 2 digits
 */
export function maskCpf(cpf: string): string {
  const cleanCpf = cpf.replace(/\D/g, "");
  if (cleanCpf.length !== 11) return cpf;
  return `***.***.*${cleanCpf.slice(7, 9)}-${cleanCpf.slice(9)}`;
}

/**
 * Generates a unique voter password
 * Used when generating passwords for authorized voters
 *
 * @param length - Password length (default: 6)
 * @returns string - A random alphanumeric password
 */
export function generateVoterPassword(length: number = 6): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // Removed ambiguous chars (I, O, 0, 1)
  let password = "";
  const randomValues = new Uint32Array(length);
  crypto.getRandomValues(randomValues);

  for (let i = 0; i < length; i++) {
    password += chars[randomValues[i] % chars.length];
  }

  return password;
}

/**
 * Generates a unique protocol number for confirmations
 * Format: CIPA-YYYY-XXXXXXXX (year + 8 random alphanumeric chars)
 *
 * @returns string - A unique protocol number
 */
export function generateProtocolNumber(): string {
  const year = new Date().getFullYear();
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ0123456789";
  let code = "";
  const randomValues = new Uint32Array(8);
  crypto.getRandomValues(randomValues);

  for (let i = 0; i < 8; i++) {
    code += chars[randomValues[i] % chars.length];
  }

  return `CIPA-${year}-${code}`;
}
